// Driver session doc: Sessions/<uid>/sessionData/<id>. The only place that id gets built.
//
// iOS mints the id as String(describing: seconds) and stores that same Double in
// Users.lastCuratedOnUnix, so String(x) is the id. Android mints String(ms) but stores
// ms / 1000.0, so its id is String(Math.round(x * 1000)).

const SESSION_ID_RE = /^\d+(\.\d+)?$/
const MISS_RETRY_MS = 15000

const lookups = new Map()

export function isSessionDocId(id) {
  return typeof id === 'string' && SESSION_ID_RE.test(id)
}

// [String(x), ms id]; [] when there is no usable lastCuratedOnUnix.
export function sessionIdCandidates(lastCuratedOnUnix) {
  const x = lastCuratedOnUnix
  if (typeof x !== 'number' || !(x > 0) || !isSessionDocId(String(x))) return []
  const ms = String(Math.round(x * 1000))
  return isSessionDocId(ms) ? [String(x), ms] : [String(x)]
}

// Android writes lastCuratedOn (ms Timestamp) in the same update as lastCuratedOnUnix;
// iOS writes whole seconds there. An exact sub-second match = Android, no read needed.
// Not Users.device / currentSession.sessionDevice: both outlive a platform switch.
export function androidSessionId(userDoc) {
  const ms = sessionIdCandidates(userDoc && userDoc.lastCuratedOnUnix)[1]
  const at = userDoc && userDoc.lastCuratedOn
  if (!ms || !at) return null
  const atMs = typeof at.toMillis === 'function' ? at.toMillis() : at instanceof Date ? at.getTime() : NaN
  return atMs === Number(ms) && atMs % 1000 !== 0 ? ms : null
}

export function sessionDocRef(db, uid, sessionDocId) {
  if (!db || !uid || !isSessionDocId(sessionDocId)) return null
  return db.collection('Sessions').doc(uid).collection('sessionData').doc(sessionDocId)
}

async function firstExisting(db, uid, ids) {
  for (const id of ids) {
    try {
      if ((await sessionDocRef(db, uid, id).get()).exists) return id
    } catch (e) {
      return null
    }
  }
  return null
}

// Doc id of the driver's latest session, '' when there is none. Otherwise a one-time
// getDoc per session, cached for the page: String(x) first (iOS), then the ms id.
// Neither there yet -> String(x), rechecked after MISS_RETRY_MS.
export function resolveSessionDocId(userDoc, { db, uid } = {}) {
  const candidates = sessionIdCandidates(userDoc && userDoc.lastCuratedOnUnix)
  if (candidates.length === 0) return Promise.resolve('')
  const android = androidSessionId(userDoc)
  if (android) return Promise.resolve(android)
  const owner = uid || userDoc.uid
  if (!db || !owner) return Promise.resolve(candidates[0])

  const key = owner + '/' + candidates[0]
  const hit = lookups.get(key)
  if (hit && (hit.found !== false || Date.now() - hit.at < MISS_RETRY_MS)) return hit.promise

  const entry = { found: undefined, at: 0 }
  entry.promise = firstExisting(db, owner, candidates).then((found) => {
    entry.found = Boolean(found)
    entry.at = Date.now()
    return found || candidates[0]
  })
  lookups.set(key, entry)
  return entry.promise
}
